import { useEffect } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { logAnalyticsEvent } from '../../../analytics/nativeAnalytics';
import { OverviewTab } from './talentPool/OverviewTab';
import { SearchTab } from './talentPool/SearchTab';
import { CampaignsTab } from './talentPool/CampaignsTab';
import { DashboardTab } from './talentPool/DashboardTab';
import { PortalPreviewTab } from './talentPool/PortalPreviewTab';
import { TemplatesTab } from './talentPool/TemplatesTab';
import { LeadsTab } from './talentPool/LeadsTab';
import { ImportTab } from './talentPool/ImportTab';
import { ToolSidebar } from './talentPool/ToolSidebar';
import { isSection, sectionLabel, type SectionId } from './talentPool/sections';

/** Tab URLs from earlier versions of this page, so links already shared still land somewhere sensible. */
const RENAMED: Record<string, SectionId> = {
  'talent-pool': 'search',
  'vetted-talent': 'search',
  community: 'leads',
  internal: 'overview',
};

/**
 * Talent Community — the tool for people FINN already knows, in three groups: Vetted Talent
 * (interviewed, not hired, from Lever), Leads (sourced or signed up themselves) and Internal Talents
 * (not in version 1). Follows design v0.6 and the screens of Anna's mockup v0.5 (Oct 2026). Still NO
 * candidate data: the screens work so the team can try the flows, but they are empty, and nothing is
 * saved or sent. The only piece wired to a backend is the Leads sign-up link builder.
 * Like the TA Action Boards, the tool has its own black sidebar inside the page (ToolSidebar); each
 * section is its own URL under /talent-pool, and People Space's sidebar keeps one entry for it.
 */
export default function TalentPoolView() {
  const { tab: param } = useParams<{ tab?: string }>();
  const renamed = param ? RENAMED[param] : undefined;
  const tab: SectionId = isSection(param) ? param : 'overview';

  useEffect(() => {
    if (!renamed && isSection(param)) void logAnalyticsEvent('talent_pool_tab_opened', { tab });
  }, [tab, renamed, param]);

  // The page always sits on a section URL, so the tool's sidebar always marks where you are.
  if (renamed) return <Navigate to={`/talent-pool/${renamed}`} replace />;
  if (!isSection(param)) return <Navigate to="/talent-pool/overview" replace />;

  return (
    <div className="flex min-h-full">
      <ToolSidebar current={tab} />
      <main className="min-w-0 flex-1 bg-[#F8F8F8] px-6 py-8">
        <div className="mx-auto max-w-6xl">
          <div className="mb-6">
            <h1 className="text-2xl font-semibold text-zinc-900">{sectionLabel(tab)}</h1>
            {tab === 'overview' && (
              <p className="mt-1 max-w-3xl text-sm text-zinc-600">
                People we already know, in one place: Vetted Talent we interviewed and did not hire, and Leads who
                signed up or were sourced. Both join only with their own consent. Version 1 builds both groups; Internal
                Talents come later. Campaigns are not saved or sent yet.
              </p>
            )}
          </div>

          {tab === 'overview' && <OverviewTab />}
          {tab === 'search' && <SearchTab />}
          {tab === 'campaigns' && <CampaignsTab />}
          {tab === 'dashboard' && <DashboardTab />}
          {tab === 'portal' && <PortalPreviewTab />}
          {tab === 'templates' && <TemplatesTab />}
          {tab === 'leads' && <LeadsTab />}
          {tab === 'import' && <ImportTab />}
        </div>
      </main>
    </div>
  );
}
